import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import challengeApi from '../challengeApi';
import Challenge from './Challenge';
import OtpInput from './OtpInput';
import { validateEmail } from '../utils/emailValidation';
import { useElectron } from '../hooks/useElectron';

const AUTH_BASE = (process.env.REACT_APP_AUTH_API_URL || 'https://auth.app.lxop.in').replace(/\/$/, '');

// Inline sign-in gate for the post preview. Email OTP + Google + GitHub +
// LinkedIn — the same methods as the main login, but it never navigates away
// from the article. On success it calls onAuthed(token, user); GitHub and
// LinkedIn leave the page and return via their /auth/*/callback routes, which
// read the stored redirect so the reader lands back on the article.
export default function PostAuthGate({ onAuthed, redirectPath }) {
  const isElectron = useElectron();
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [challengeData, setChallengeData] = useState({ challenge_id: '', answer: '' });
  const [challengeError, setChallengeError] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [gLoading, setGLoading] = useState(false);
  const [ghLoading, setGhLoading] = useState(false);
  const [liLoading, setLiLoading] = useState(false);
  const googleBtnRef = useRef(null);

  const onGoogle = useCallback(async (payload) => {
    setGLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/google', payload);
      onAuthed(data.token, data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Google sign-in failed. Please try again.');
    } finally { setGLoading(false); }
  }, [onAuthed]);

  // Web Google: render the GSI button.
  useEffect(() => {
    if (isElectron) return;
    const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!CLIENT_ID) return;
    const init = () => {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({ client_id: CLIENT_ID, callback: (r) => onGoogle({ credential: r.credential }) });
      // GSI caps button width at 400px — match the column (capped at 400) so it
      // lines up exactly with the full-width GitHub button below it.
      const w = Math.min(400, Math.round(googleBtnRef.current.offsetWidth) || 400);
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'filled_black', size: 'large', width: w, text: 'continue_with', shape: 'pill', logo_alignment: 'left',
      });
    };
    if (window.google) { init(); return; }
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client'; s.async = true; s.defer = true; s.onload = init;
    document.head.appendChild(s);
    return () => { if (document.head.contains(s)) document.head.removeChild(s); };
  }, [isElectron, onGoogle]);

  const handleElectronGoogle = useCallback(async () => {
    if (!window.googleAuth) return;
    setGLoading(true); setError('');
    try {
      const r = await window.googleAuth.start(process.env.REACT_APP_GOOGLE_DESKTOP_CLIENT_ID || '');
      if (!r?.ok) { setError(r?.error || 'Google sign-in was cancelled.'); setGLoading(false); return; }
      await onGoogle({ code: r.code, redirect_uri: r.redirectUri });
    } catch { setError('Google sign-in failed. Please try again.'); setGLoading(false); }
  }, [onGoogle]);

  const handleGithub = useCallback(async () => {
    setError('');
    if (isElectron) {
      if (!window.githubAuth) { setError('GitHub sign-in is unavailable in this app version.'); return; }
      setGhLoading(true);
      try {
        const r = await window.githubAuth.start(process.env.REACT_APP_GITHUB_DESKTOP_CLIENT_ID || '');
        if (!r?.ok) { setError(r?.error || 'GitHub sign-in failed.'); setGhLoading(false); return; }
        const res = await fetch(`${AUTH_BASE}/auth/github`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: r.code, redirect_uri: r.redirectUri }),
        });
        const data = await res.json();
        if (!res.ok || !data.token) { setError(data.error || 'GitHub sign-in failed.'); setGhLoading(false); return; }
        onAuthed(data.token, data.user);
      } catch (err) { setError('GitHub sign-in failed.'); }
      finally { setGhLoading(false); }
    } else {
      const clientId = process.env.REACT_APP_GITHUB_CLIENT_ID || '';
      if (!clientId) { setError('GitHub sign-in is not configured.'); return; }
      const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('lxops_github_state', state);
      sessionStorage.setItem('lxops_github_redirect', redirectPath || window.location.pathname);
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: `${window.location.origin}/auth/github/callback`,
        scope: 'read:user user:email', state, allow_signup: 'true',
      });
      window.location.href = `https://github.com/login/oauth/authorize?${params}`;
    }
  }, [isElectron, onAuthed, redirectPath]);

  // Same authorization-code flow as the main login page. On the web this is a
  // full-page redirect; the article path is stashed so LinkedinCallback brings
  // the reader back here rather than dropping them on /posts.
  const handleLinkedin = useCallback(async () => {
    setError('');
    if (isElectron) {
      if (!window.linkedinAuth) { setError('LinkedIn sign-in is unavailable in this app version.'); return; }
      setLiLoading(true);
      try {
        const r = await window.linkedinAuth.start(process.env.REACT_APP_LINKEDIN_DESKTOP_CLIENT_ID || '');
        if (!r?.ok) { setError(r?.error || 'LinkedIn sign-in failed.'); setLiLoading(false); return; }
        const res = await fetch(`${AUTH_BASE}/auth/linkedin`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: r.code, redirect_uri: r.redirectUri }),
        });
        const data = await res.json();
        if (!res.ok || !data.token) { setError(data.error || 'LinkedIn sign-in failed.'); setLiLoading(false); return; }
        onAuthed(data.token, data.user);
      } catch (err) { setError('LinkedIn sign-in failed.'); }
      finally { setLiLoading(false); }
    } else {
      const clientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID || '';
      if (!clientId) { setError('LinkedIn sign-in is not configured.'); return; }
      const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('lxops_linkedin_state', state);
      sessionStorage.setItem('lxops_linkedin_redirect', redirectPath || window.location.pathname);
      const params = new URLSearchParams({
        response_type: 'code',
        client_id:     clientId,
        redirect_uri:  `${window.location.origin}/auth/linkedin/callback`,
        scope:         'openid profile email',
        state,
      });
      window.location.href = `https://www.linkedin.com/oauth/v2/authorization?${params}`;
    }
  }, [isElectron, onAuthed, redirectPath]);

  const sendOtp = async (e) => {
    e.preventDefault();
    setError('');
    const emailErr = validateEmail(email);
    if (emailErr) { setEmailError(emailErr); return; }
    if (!challengeData.answer) { setChallengeError('Please solve the security check above.'); return; }
    setLoading(true);
    try {
      let proof;
      try {
        const { data } = await challengeApi.post('/challenge/verify', { challenge_id: challengeData.challenge_id, answer: Number(challengeData.answer) });
        proof = data.proof;
      } catch (err) { setChallengeError(err.response?.data?.error || 'Incorrect answer. Try again.'); setLoading(false); return; }
      const { data } = await api.post('/auth/login', { email, proof });
      setInfo(data.message || 'We emailed you a 6-digit code.');
      setStep('otp');
    } catch (err) {
      const d = err.response?.data;
      setError(d?.needs_verification ? 'Email not verified. Please sign up first.' : (d?.error || 'Failed to send code. Try again.'));
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setError('Enter the full 6-digit code.'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/verify-otp', { email, otp });
      onAuthed(data.token, data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally { setLoading(false); }
  };

  const googleEnabled = isElectron ? process.env.REACT_APP_GOOGLE_DESKTOP_CLIENT_ID : process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const githubEnabled = isElectron ? process.env.REACT_APP_GITHUB_DESKTOP_CLIENT_ID : process.env.REACT_APP_GITHUB_CLIENT_ID;
  // LinkedIn has no desktop loopback bridge yet, so on Electron it only shows
  // when the app exposes window.linkedinAuth. Same rule as the login page.
  const linkedinEnabled = isElectron
    ? (process.env.REACT_APP_LINKEDIN_DESKTOP_CLIENT_ID && typeof window !== 'undefined' && window.linkedinAuth)
    : process.env.REACT_APP_LINKEDIN_CLIENT_ID;

  return (
    <div className="w-full max-w-[400px] mx-auto text-left">
      {step === 'email' && (googleEnabled || githubEnabled || linkedinEnabled) && (
        <div className="mb-4">
          {googleEnabled && (gLoading ? (
            <div className="flex items-center justify-center gap-2 w-full py-3 rounded-xl mb-3" style={{ background: '#1f1f1f', border: '1px solid #3c3c3c' }}>
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /><span className="text-sm text-gray-400">Signing in…</span>
            </div>
          ) : isElectron ? (
            <button type="button" onClick={handleElectronGoogle} className="w-full flex items-center justify-center gap-3 py-3 rounded-full mb-3 text-sm font-medium transition-colors hover:bg-[#262626]" style={{ background: '#1f1f1f', border: '1px solid #3c3c3c', color: '#e5e7eb' }}>
              <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 6.293C4.672 4.166 6.656 3.58 9 3.58z"/></svg>
              Continue with Google
            </button>
          ) : (
            <div ref={googleBtnRef} className="w-full mb-3 flex justify-center" style={{ minHeight: '44px' }} />
          ))}

          {githubEnabled && (
            <button type="button" onClick={handleGithub} disabled={ghLoading} className="w-full flex items-center justify-center gap-3 py-3 rounded-full text-sm font-medium transition-colors hover:bg-[#262626] disabled:opacity-50" style={{ background: '#1f1f1f', border: '1px solid #3c3c3c', color: '#e5e7eb' }}>
              {ghLoading ? (<><div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />Redirecting…</>) : (<><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 .5C5.7.5.5 5.7.5 12c0 5.1 3.3 9.4 7.9 10.9.6.1.8-.3.8-.6v-2c-3.2.7-3.9-1.4-3.9-1.4-.5-1.3-1.3-1.7-1.3-1.7-1.1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.7-1.6-2.6-.3-5.3-1.3-5.3-5.8 0-1.3.5-2.3 1.2-3.1-.1-.3-.5-1.5.1-3.2 0 0 1-.3 3.3 1.2a11.5 11.5 0 0 1 6 0C17 4.2 18 4.5 18 4.5c.6 1.7.2 2.9.1 3.2.8.8 1.2 1.8 1.2 3.1 0 4.5-2.7 5.5-5.3 5.8.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6 4.6-1.5 7.9-5.8 7.9-10.9C23.5 5.7 18.3.5 12 .5z"/></svg>Continue with GitHub</>)}
            </button>
          )}

          {linkedinEnabled && (
            <button type="button" onClick={handleLinkedin} disabled={liLoading} className={`w-full flex items-center justify-center gap-3 py-3 rounded-full text-sm font-medium transition-colors hover:bg-[#262626] disabled:opacity-50${githubEnabled ? ' mt-3' : ''}`} style={{ background: '#1f1f1f', border: '1px solid #3c3c3c', color: '#e5e7eb' }}>
              {liLoading ? (<><div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />Redirecting…</>) : (<><svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 0 1-2.06-2.07 2.06 2.06 0 1 1 2.06 2.07zm1.78 13.02H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"/></svg>Continue with LinkedIn</>)}
            </button>
          )}

          <div className="flex items-center gap-3 mt-4">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--lx-border)' }} />
            <span className="text-xs text-gray-600">or with email</span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--lx-border)' }} />
          </div>
        </div>
      )}

      {info && <div className="mb-3 px-4 py-2.5 bg-indigo-950/40 border border-indigo-800/40 rounded-xl text-indigo-200 text-sm">{info}</div>}
      {error && <div className="mb-3 px-4 py-2.5 bg-red-950/40 border border-red-800/50 rounded-xl text-red-400 text-sm">{error}</div>}

      {step === 'email' ? (
        <form onSubmit={sendOtp} className="space-y-3">
          <input type="email" value={email} required placeholder="you@email.com" disabled={loading}
            onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
            className={`input-field ${emailError ? 'border-red-600' : ''}`} />
          {emailError && <p className="text-red-400 text-xs">{emailError}</p>}
          <Challenge onChange={(d) => { setChallengeData(d); setChallengeError(''); }} error={challengeError} disabled={loading} />
          {challengeError && <p className="text-orange-400 text-xs">{challengeError}</p>}
          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Sending code…</> : 'Email me a sign-in code →'}
          </button>
        </form>
      ) : (
        <form onSubmit={verifyOtp} className="space-y-4">
          <p className="text-sm text-gray-400 text-center">Enter the 6-digit code sent to <span className="text-gray-200">{email}</span></p>
          <OtpInput length={6} onChange={setOtp} disabled={loading} />
          <button type="submit" disabled={loading || otp.length !== 6} className="btn-primary w-full">
            {loading ? 'Verifying…' : 'Verify & continue →'}
          </button>
          <button type="button" onClick={() => { setStep('email'); setOtp(''); setError(''); setInfo(''); }} className="w-full text-sm text-gray-500 hover:text-gray-300">← Use a different email</button>
        </form>
      )}
    </div>
  );
}
